import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Phone, Bell, MessageSquare, CheckCircle, AlertCircle, Send, Settings } from 'lucide-react';
import { toast } from 'sonner';



const SMSPreferences = ({ user }) => {
  const [preferences, setPreferences] = useState({
    phone_number: '',
    sms_enabled: false,
    sms_rate_alerts: true,
    sms_renewal_reminders: true,
    sms_onboarding_reminders: true,
    verified: false
  });
  const [smsStatus, setSmsStatus] = useState({ configured: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [prefsRes, statusRes] = await Promise.all([
        api.get(`/api/sms/preferences`),
        api.get(`/api/sms/status`)
      ]);
      
      setPreferences(prefsRes.data);
      setPhoneInput(prefsRes.data.phone_number || '');
      setSmsStatus(statusRes.data);
    } catch (error) {
      console.error('Error fetching SMS preferences:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const formatPhoneDisplay = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };
  
  const savePreferences = async () => {
    try {
      setSaving(true);
      await api.post(`/api/sms/preferences`, {
        phone_number: phoneInput,
        sms_enabled: preferences.sms_enabled,
        sms_rate_alerts: preferences.sms_rate_alerts,
        sms_renewal_reminders: preferences.sms_renewal_reminders,
        sms_onboarding_reminders: preferences.sms_onboarding_reminders
      });
      
      toast.success('SMS preferences saved');
      
      // If phone number changed, need to verify again
      if (phoneInput !== preferences.phone_number) {
        setPreferences(prev => ({ ...prev, verified: false, phone_number: phoneInput }));
      }
      
      fetchData();
    } catch (error) {
      console.error('Error saving SMS preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };
  
  const sendVerificationCode = async () => {
    try {
      setVerifying(true);
      await api.post(`/api/sms/verify`, {});
      setShowVerification(true);
      toast.success('Verification code sent to your phone');
    } catch (error) {
      console.error('Error sending verification:', error);
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setVerifying(false);
    }
  };
  
  const confirmVerification = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    try {
      setVerifying(true);
      await api.post(`/api/sms/verify/confirm?code=${verificationCode}`, {});
      toast.success('Phone number verified!');
      setShowVerification(false);
      setVerificationCode('');
      fetchData();
    } catch (error) {
      console.error('Error confirming verification:', error);
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };
  
  const sendTestSMS = async () => {
    try {
      await api.post(`/api/sms/test`, {});
      toast.success('Test SMS sent!');
    } catch (error) {
      console.error('Error sending test SMS:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    }
  };
  
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div data-testid="sms-preferences" className="bg-card border border-border rounded-xl p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <MessageSquare className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold">SMS Notifications</h3>
            <p className="text-sm text-muted-foreground">Get important updates via text message</p>
          </div>
        </div>
        {smsStatus.configured ? (
          <span className="flex items-center gap-1 text-xs bg-green-500/10 text-green-400 px-2 py-1 rounded-full">
            <CheckCircle className="h-3 w-3" />
            Available
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs bg-yellow-500/10 text-yellow-400 px-2 py-1 rounded-full">
            <AlertCircle className="h-3 w-3" />
            Not Configured
          </span>
        )}
      </div>
      
      {!smsStatus.configured && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 text-sm">
          <p className="text-yellow-400 font-medium">SMS service not configured</p>
          <p className="text-muted-foreground mt-1">
            Twilio credentials are required to enable SMS notifications. Contact support for setup.
          </p>
        </div>
      )}
      
      {smsStatus.configured && (
        <>
          {/* Phone Number Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium">Phone Number</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="tel"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                  placeholder="(416) 555-1234"
                />
              </div>
              {preferences.phone_number && !preferences.verified && (
                <button
                  onClick={sendVerificationCode}
                  disabled={verifying}
                  className="px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm whitespace-nowrap"
                >
                  {verifying ? 'Sending...' : 'Verify'}
                </button>
              )}
              {preferences.verified && (
                <span className="flex items-center gap-1 px-3 py-2.5 bg-green-500/10 text-green-400 rounded-lg text-sm">
                  <CheckCircle className="h-4 w-4" />
                  Verified
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Canadian phone numbers only. Standard messaging rates may apply.
            </p>
          </div>
          
          {/* Verification Code Input */}
          {showVerification && (
            <div className="bg-muted/30 border border-border rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium">Enter the 6-digit code sent to your phone:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="flex-1 px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary text-center font-mono text-lg tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                />
                <button
                  onClick={confirmVerification}
                  disabled={verifying || verificationCode.length !== 6}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm disabled:opacity-50"
                >
                  {verifying ? 'Verifying...' : 'Confirm'}
                </button>
              </div>
              <button
                onClick={sendVerificationCode}
                disabled={verifying}
                className="text-sm text-primary hover:underline"
              >
                Resend code
              </button>
            </div>
          )}
          
          {/* Notification Preferences */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Notification Types
            </h4>
            
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">Rate Alerts</p>
                    <p className="text-xs text-muted-foreground">Get notified when rates drop to your target</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.sms_rate_alerts}
                  onChange={(e) => setPreferences({ ...preferences, sms_rate_alerts: e.target.checked })}
                  className="h-5 w-5 rounded border-border text-primary focus:ring-primary"
                />
              </label>
              
              <label className="flex items-center justify-between p-3 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-orange-400" />
                  <div>
                    <p className="font-medium">Renewal Reminders</p>
                    <p className="text-xs text-muted-foreground">Reminders at 90, 60, 30, 14, and 7 days before renewal</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.sms_renewal_reminders}
                  onChange={(e) => setPreferences({ ...preferences, sms_renewal_reminders: e.target.checked })}
                  className="h-5 w-5 rounded border-border text-primary focus:ring-primary"
                />
              </label>
              
              <label className="flex items-center justify-between p-3 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-blue-400" />
                  <div>
                    <p className="font-medium">Onboarding Reminders</p>
                    <p className="text-xs text-muted-foreground">Complete your profile setup reminders</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.sms_onboarding_reminders}
                  onChange={(e) => setPreferences({ ...preferences, sms_onboarding_reminders: e.target.checked })}
                  className="h-5 w-5 rounded border-border text-primary focus:ring-primary"
                />
              </label>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <button
              onClick={savePreferences}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>
            
            {preferences.verified && (
              <button
                onClick={sendTestSMS}
                className="flex items-center gap-2 px-4 py-2.5 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
              >
                <Send className="h-4 w-4" />
                Test SMS
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SMSPreferences;
